import { app } from "@/app.context";
import {
  PublicActivityLocationDTO,
  PublicActivityRegistrationLinkDTO,
  PublicStudyProgramDto,
} from "lib/eduConfigurationServiceClient";
import { computed } from "vue";

// Link filters
export function linkMatchesQuery(searchQuery: string | undefined) {
  return (link: PublicActivityRegistrationLinkDTO) =>
    !searchQuery ||
    link.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    link.description?.toLowerCase().includes(searchQuery.toLowerCase());
}
export function linkMatchesActivityTypeId(activityTypeId: string | undefined) {
  return (link: PublicActivityRegistrationLinkDTO) =>
    !activityTypeId || link.activityTypeId === activityTypeId;
}

export function linkMatchesActivityTypes(types: string[]) {
  return (link: PublicActivityRegistrationLinkDTO) =>
    types.length === 0 || types.includes(link.type);
}

export function linkMatchesMonths(months: string[]) {
  return (link: PublicActivityRegistrationLinkDTO) =>
    months.length === 0 ||
    months.includes(link.startDateTime.toFormat("MMMM yyyy"));
}

export function linkMatchesCities(
  activityLocations: PublicActivityLocationDTO[],
) {
  return (cities: string[]) => (link: PublicActivityRegistrationLinkDTO) => {
    if (cities.length === 0) return true;

    return link.activityLocationIds
      .map((id) => activityLocations.find((loc) => loc.id === id))
      .some((loc) => loc?.city && cities.includes(loc.city));
  };
}

export const modeOnline = computed(() => app.texts.activity.online);
export const modePhysical = computed(() => app.texts.activity.physical);

export function linkMatchesModes(modes: string[]) {
  return (link: PublicActivityRegistrationLinkDTO) =>
    modes.length === 0 ||
    (modes.includes(modeOnline.value) && link.isOnline) ||
    (modes.includes(modePhysical.value) && !link.isOnline);
}

export function linkMatchesLinkInFilter(
  linkFilter: PublicActivityRegistrationLinkDTO[] | undefined,
) {
  return (link: PublicActivityRegistrationLinkDTO) =>
    !linkFilter || linkFilter.includes(link);
}

export function linkMatchesStudyProgramInFilter(
  studyProgramFilter: PublicStudyProgramDto[] | undefined,
) {
  return (link: PublicActivityRegistrationLinkDTO) =>
    !studyProgramFilter ||
    studyProgramFilter.some((spf) => link.studyProgramIds.includes(spf.id));
}

// Activity location filters
export function activityLocationMatchesCities(cities: string[]) {
  return (activityLocation: PublicActivityLocationDTO) =>
    cities.length === 0 ||
    (!!activityLocation.city && cities.includes(activityLocation.city));
}

// Study program filters
export function studyProgramMatchesId(studyProgramId: string | undefined) {
  return (studyProgram: PublicStudyProgramDto) =>
    !studyProgramId || studyProgram.id === studyProgramId;
}

export function studyProgramMatchesQuery(searchQuery: string | undefined) {
  return (studyProgram: PublicStudyProgramDto) =>
    !searchQuery ||
    studyProgram.displayName.toLowerCase().includes(searchQuery.toLowerCase());
}

export function studyProgramMatchesInFilter(
  studyProgramFilter: PublicStudyProgramDto[] | undefined,
) {
  return (studyProgram: PublicStudyProgramDto) =>
    !studyProgramFilter || studyProgramFilter.includes(studyProgram);
}

export function studyProgramMatchesAreaOfInterest(
  areaOfInterestId: string | undefined,
) {
  return (studyProgram: PublicStudyProgramDto) =>
    !areaOfInterestId ||
    studyProgram.areaOfInterestIds
      .map((id) => id.toLowerCase())
      .includes(areaOfInterestId!.toLowerCase());
}

export function studyProgramMatchesAreasOfInterest(areasOfInterest: string[]) {
  return (studyProgram: PublicStudyProgramDto) =>
    areasOfInterest.length === 0 ||
    studyProgram.areasOfInterest.some((interest) =>
      areasOfInterest.includes(interest),
    );
}

export function studyProgramMatchesLanguage(
  studyProgramLanguage: string | undefined,
) {
  return (studyProgram: PublicStudyProgramDto) =>
    !studyProgramLanguage ||
    studyProgram.languageLocale?.toLowerCase() ==
      studyProgramLanguage!.toLowerCase();
}

export function studyProgramMatchesLanguages(languages: string[]) {
  return (studyProgram: PublicStudyProgramDto) =>
    languages.length === 0 ||
    (!!studyProgram.language && languages.includes(studyProgram.language));
}

export function studyProgramMatchesModes(modes: string[]) {
  return (studyProgram: PublicStudyProgramDto) =>
    modes.length === 0 ||
    (!!studyProgram.mode && modes.includes(studyProgram.mode));
}

export function studyProgramMatchesLevels(levels: string[]) {
  return (studyProgram: PublicStudyProgramDto) =>
    levels.length === 0 ||
    (!!studyProgram.level && levels.includes(studyProgram.level));
}

export function studyProgramMatchesLocations(locations: string[]) {
  return (studyProgram: PublicStudyProgramDto) =>
    locations.length === 0 ||
    studyProgram.locations.some((location) => locations.includes(location));
}

export function studyProgramMatchesDepartments(departments: string[]) {
  return (studyProgram: PublicStudyProgramDto) =>
    departments.length === 0 ||
    (!!studyProgram.department &&
      departments.includes(studyProgram.department));
}
